import { Box, Chip, ChipProps, Tooltip } from "@mui/material";
import { ContentNode, ContentNodeType } from "@/app/graphql/codegen";
import { getCountyCode } from "@chat/components/sidebar/documents/CustomTreeItem";
import { ReactNode } from "react";
import { getUnifiedDocumentLabel } from "@/utils/documentLabels";

// Unterstützte Chip-Typen
type ChipType = "standard" | "graph" | "company" | "attachment";

interface DocChipProps {
    // ContentNode ist optional, wenn spezielle Chip-Typen verwendet werden
    node?: ContentNode;
    onClick?: () => void;
    onDelete?: () => void;
    size?: "tiny" | "small" | "medium";
    flagStyle?: React.CSSProperties;
    truncateLabel?: boolean;
    maxLabelLength?: number;
    showTooltip?: boolean;
    tooltipPlacement?: "top" | "bottom" | "left" | "right";
    // Neues Feld für spezielle Chip-Typen
    chipType?: ChipType;
    tooltipTitle?: string;
    /** Wenn true, Chip wird optisch abgeblendet (z.B. für versteckte Selektionen) */
    dimmed?: boolean;
    /** Für attachment-Chips: Dateiname */
    fileName?: string;
    /** Für attachment-Chips: Dateigröße in Bytes */
    fileSize?: number;
    /** Für attachment-Chips: Token-Anzahl */
    fileTokens?: number;
}

const DocChip = ({
    node,
    onClick,
    onDelete,
    size,
    flagStyle,
    truncateLabel = false,
    maxLabelLength = 36,
    showTooltip = false,
    tooltipPlacement = "top",
    chipType = "standard",
    tooltipTitle,
    dimmed = false,
    fileName,
    fileSize,
    fileTokens,
}: DocChipProps) => {

    // Basis-Variablen für alle Chip-Typen
    let emoji: string = "";
    let textLabel: string = "";
    let countryCode: string | null = null;

    // Helper function to format file size
    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    };

    // --------------------------------------------------------
    // Logik für Spezial-Chip-Typen (graph, company, attachment)
    // --------------------------------------------------------
    if (chipType === "graph" || chipType === "company" || chipType === "attachment") {
        // Spezifische Inhalte je nach Spezial-Typ
        if (chipType === "graph") {
            emoji = "🔍";
            textLabel = "SmartSearch";
            tooltipTitle = tooltipTitle ?? "SmartSearch-Modus: KI nutzt die 'SmartSearch' Funktion für Antworten";
        } else if (chipType === "company") {
            emoji = "🏢";
            textLabel = "Firmenkontext";
            tooltipTitle = tooltipTitle ?? "Firmenkontext: KI berücksichtigt firmenspezifische Daten";
        } else if (chipType === "attachment") {
            emoji = "📎";
            textLabel = fileName || "Anhang";
            
            // Truncate filename if needed
            if (truncateLabel && textLabel.length > maxLabelLength) {
                textLabel = textLabel.substring(0, maxLabelLength - 3) + "...";
            }
            
            // Build detailed tooltip
            const sizeStr = fileSize ? formatFileSize(fileSize) : "";
            //const tokensStr = fileTokens ? `${fileTokens.toLocaleString()} Tokens` : "";
            const pagesStr = fileTokens ? `ca. ${Math.ceil(fileTokens / 800)} Seiten` : "";
            
            tooltipTitle = tooltipTitle ?? `${fileName}${sizeStr ? ` (${sizeStr})` : ""}${/*tokensStr ? `\n${tokensStr}` : ""*/''}${pagesStr ? ` • ${pagesStr}` : ""}`;
        }
    }
    // --------------------------------------------------------
    // Logik für Standard ContentNode-basierte Chips
    // --------------------------------------------------------
    else if (node) {
        // Hochheitsgebiet aus Node oder zugehörigem Dokument ermitteln
        const jurisdiction = node.metadata?.jurisdiction || node.associatedOfficialDocument?.jurisdiction || node.associatedOfficialDocument?.metadata?.jurisdiction || "NONE";
        countryCode = getCountyCode(jurisdiction);

        // Emoji basierend auf Node-Typ setzen
        emoji = node.type === ContentNodeType.Summary || node?.metadata?.subdirectory ? "📄" : "";

        // Verwende die einheitliche Dokumentenbezeichnungsfunktion
        // Bei Unterabschnitten zeige den Namen des übergeordneten Dokuments als Präfix
        const showParentPrefix = node.type === ContentNodeType.SubSection;

        textLabel = getUnifiedDocumentLabel(node, {
            showType: false,
            truncate: truncateLabel,
            maxLength: maxLabelLength,
            showParentPrefix,
        });

        // Vollständige Textbezeichnung für Tooltip
        tooltipTitle = getUnifiedDocumentLabel(node, {
            showType: false,
            showParentPrefix,
        });

        // Wenn Chip abgeblendet dargestellt wird, Hinweis anhängen
        if (dimmed) {
            tooltipTitle = `${tooltipTitle} (aktuell ausgeblendet)`;
        }
    }

    // --------------------------------------------------------
    // Gemeinsame Styling- und Rendering-Logik für alle Chip-Typen
    // --------------------------------------------------------

    // Standard-Flaggenstil
    const defaultFlagStyle = {
        marginLeft: "12px",
        marginRight: 0,
    };

    // Label mit Emoji erstellen
    const labelContent: ReactNode = (
        <>
            {emoji && (
                <Box
                    component="span"
                    sx={{
                        opacity: 0.6,
                        mr: 0.5,
                        fontSize: "0.8em",
                        display: "inline-block",
                        transform: "translateY(-1px)", // Emoji leicht nach oben verschieben
                    }}
                >
                    {emoji}
                </Box>
            )}
            {textLabel}
        </>
    );

    // Spezielle Stile für "tiny" Chips
    const tinyChipStyles =
        size === "tiny"
            ? {
                  height: "18px",
                  "& .MuiChip-label": {
                      padding: "0 6px",
                      fontSize: "0.6rem",
                      lineHeight: 1.2,
                  },
                  "& .MuiChip-icon": {
                      fontSize: "0.65rem",
                      marginLeft: "4px",
                      marginRight: "-6px",
                  },
              }
            : {};

    // Basis-Chip-Props
    const chipProps: ChipProps = {
        label: labelContent,
        size: size === "tiny" ? "small" : size,
        sx: {
            fontSize: size === "tiny" ? "0.75em" : "1em",
            margin: "2px",
            background: chipType === "graph" ? "semantic.highlight.primary" : undefined,
            opacity: dimmed ? 0.4 : undefined,
            ...tinyChipStyles,
        },
    };

    // Handler hinzufügen, wenn vorhanden
    if (onClick) chipProps.onClick = onClick;
    if (onDelete) chipProps.onDelete = onDelete;

    // Länderflagge als Icon hinzufügen, wenn Ländercode vorhanden
    if (countryCode) {
        chipProps.icon = (
            <Box
                className={`fi fi-${countryCode}`}
                sx={{
                    opacity: 0.5,
                    borderRadius: "4px",
                    width: size === "tiny" ? 10 : 15,
                }}
                style={{ ...defaultFlagStyle, ...flagStyle }}
            />
        );
    }

    // Chip mit oder ohne Tooltip rendern
    return showTooltip ? (
        <Tooltip title={tooltipTitle} placement={tooltipPlacement}>
            <Chip {...chipProps} />
        </Tooltip>
    ) : (
        <Chip {...chipProps} />
    );
};

export default DocChip;
